import IconArrowLeft from 'assets/icons/arrow-left-bold.svg';
import IconArrowRight from 'assets/icons/arrow-right-bold.svg';
import ModalSlideUp from 'components/modal-slide-up';
import useLockBodyScroll from 'hooks/common/use-lock-body-scroll';
import { ProductGallery } from 'modules/product-details/components/product-details-header';
import { StaticPDPProduct } from 'modules/product-details/types';
import dynamic from 'next/dynamic';
import React from 'react';
import Slider from 'react-slick';
import { gtm, zoomElement } from 'tracking';
import { getShopifyImageUrl } from 'utils/shopify';
import CarouselSelector from '../carousel-selector';
import ImagesCarousel from './images-carousel';

const PanZoom = dynamic(() => import('./pan-zoom'), { ssr: false });

interface Props {
  imageBackgroundColor?: string;
  firstOpenGalleryTrackingRef: React.MutableRefObject<boolean>;
  product: StaticPDPProduct;
  productGallery: ProductGallery[];
  selectedGalleryItemIndex: number;
  setSelectedGalleryItemIndex: React.Dispatch<React.SetStateAction<number>>;
  setIsGalleryOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
interface Handle {
  goToIndex: (index: number) => void;
  openGallery: () => void;
}

const FullScreenGallery: React.ForwardRefRenderFunction<Handle, Props> = (
  props,
  ref
) => {
  const $slider = React.useRef<Slider>(null);
  const [open, setOpen] = React.useState(false);
  const [zoom, setZoom] = React.useState(1);

  React.useImperativeHandle(ref, () => ({
    goToIndex: (index: number) => {
      $slider.current?.slickGoTo(index);
    },
    openGallery: () => {
      setOpen(true);
      setIsGalleryOpen(true);
    },
  }));

  const {
    firstOpenGalleryTrackingRef,
    imageBackgroundColor,
    product,
    productGallery,
    selectedGalleryItemIndex,
    setSelectedGalleryItemIndex,
    setIsGalleryOpen,
  } = props;

  useLockBodyScroll(open);

  const onDoubleTap = React.useCallback(() => {
    gtm(
      zoomElement({
        element: ['PDP Images'],
      })
    );
    setZoom(zoom === 1 ? 2.5 : 1);
  }, [zoom]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <ModalSlideUp
      backdropClassName="bg-other-black/50"
      containerBackgroundColor={imageBackgroundColor}
      containerClassName="lg:max-w-[calc(100vw-32px)] lg:max-h-[calc(100vh-32px)]"
      headerClassName="p-0"
      onClose={() => {
        setOpen(false);
        setIsGalleryOpen(false);
        firstOpenGalleryTrackingRef.current = true;
      }}
      open={open}
      swipeToClose
    >
      <div className="relative">
        <button
          className="absolute left-0 z-10 ml-3 hidden h-full w-1/4 items-center opacity-0 transition-opacity hover:opacity-100 lg:flex"
          onClick={() => {
            $slider.current?.slickPrev();
          }}
        >
          <IconArrowLeft className="h-6 w-6" />
        </button>
        <button
          className="absolute right-0 z-10 mr-3 hidden h-full w-1/4 items-center justify-end opacity-0 transition-opacity hover:opacity-100 lg:flex"
          onClick={() => {
            $slider.current?.slickNext();
          }}
        >
          <IconArrowRight className="h-6 w-6" />
        </button>
        <div suppressHydrationWarning className="w-full">
          <Slider
            afterChange={(index: number) => setSelectedGalleryItemIndex(index)}
            arrows={false}
            dots={false}
            draggable={false}
            initialSlide={selectedGalleryItemIndex}
            ref={$slider}
            swipe={false}
            swipeToSlide={false}
          >
            {productGallery.map((image, index) => {
              if (!image.imageData) return null;
              return (
                <div
                  key={index}
                  className="h-[calc(100svh-104px)] flex-1 overflow-hidden lg:h-auto"
                >
                  <PanZoom
                    zoom={zoom}
                    onSwipeLeft={() => {
                      $slider.current?.slickNext();
                      setZoom(1);
                    }}
                    onSwipeRight={() => {
                      $slider.current?.slickPrev();
                      setZoom(1);
                    }}
                    onDoubleTap={onDoubleTap}
                    onZoom={(newZoom) => {
                      gtm(
                        zoomElement({
                          element: ['PDP Images'],
                        })
                      );
                      setZoom(newZoom);
                    }}
                  >
                    {open ? (
                      <img
                        loading="lazy"
                        src={getShopifyImageUrl(
                          image.imageData.originalSrc,
                          1600,
                          1600
                        )}
                        alt={image.imageData.altText || product.title}
                        className="h-[calc(100svh-104px)] w-full object-contain object-center lg:h-[calc(100vh-272px)]"
                      />
                    ) : null}
                  </PanZoom>
                </div>
              );
            })}
          </Slider>
          <CarouselSelector
            images={product.images}
            selectedGalleryItemIndex={selectedGalleryItemIndex}
            onClick={(index: number) => {
              $slider.current?.slickGoTo(index);
              setZoom(1);
            }}
          />
        </div>
      </div>
      <div>
        <ImagesCarousel
          classNames="bg-other-white -mx-3 -mb-6 rounded-b-lg p-4"
          imageBackgroundColor={imageBackgroundColor}
          images={productGallery}
          product={product}
          setSelectedGalleryItemIndex={setSelectedGalleryItemIndex}
          sliderRef={$slider}
        />
      </div>
    </ModalSlideUp>
  );
};

export default React.forwardRef(FullScreenGallery);
